import { Platform } from 'react-native';
import type { ErrorUtils as RNErrorUtils } from 'react-native';

let installed = false;

/**
 * Installs process-wide error handlers so unhandled errors and promise
 * rejections are logged instead of silently killing the app.
 *
 * - Native: wraps the existing ErrorUtils global handler (RN's dev redbox
 *   and fatal handling are preserved by delegating to the previous handler).
 * - Web: logs unhandled promise rejections, which otherwise surface only in
 *   the browser console. Uncaught errors are already handled by
 *   window.onerror in index.web.js.
 *
 * Safe to call multiple times; only the first call installs handlers.
 */
export function installGlobalErrorHandlers(): void {
  if (installed) return;
  installed = true;

  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return;
    // The RN TypeScript config has no DOM lib, so the event lacks the
    // PromiseRejectionEvent shape; read `reason` defensively instead.
    window.addEventListener('unhandledrejection', event => {
      const reason = (event as { reason?: unknown }).reason;
      console.error('Unhandled promise rejection:', reason);
      event.preventDefault();
    });
    return;
  }

  const errorUtils = (globalThis as { ErrorUtils?: RNErrorUtils }).ErrorUtils;
  if (!errorUtils) return;
  const previousHandler = errorUtils.getGlobalHandler();
  errorUtils.setGlobalHandler((error, isFatal) => {
    console.error(`Global ${isFatal ? 'fatal ' : ''}JS error:`, error);
    previousHandler?.(error, isFatal);
  });
}
